/**
 * 订单列表表格列配置
 */

import React from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { OrderTableRow } from './types';

const { Text } = Typography;

export const getOrderColumns = () => [
    {
        title: '订单号',
        dataIndex: 'amazon_order_id',
        key: 'amazon_order_id',
        width: 170,
        fixed: 'left' as const,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => <Text copyable style={{ fontSize: 13 }}>{text}</Text>,
    },
    {
        title: '时间',
        dataIndex: 'purchase_date',
        key: 'purchase_date',
        width: 100,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => <span style={{ fontSize: 12 }}>{dayjs(text).format('YYYY-MM-DD')}</span>,
    },
    {
        title: '店铺',
        dataIndex: 'shop_name',
        key: 'shop_name',
        width: 90,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => <Tag color="blue" style={{ margin: 0 }}>{text}</Tag>
    },
    {
        title: '状态',
        dataIndex: 'order_status',
        key: 'order_status',
        width: 90,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => (
            <Tag color={text === 'Shipped' ? 'green' : (text === 'Canceled' ? 'red' : 'orange')}>
                {text}
            </Tag>
        )
    },
    {
        title: '渠道',
        dataIndex: 'fulfillment_channel',
        key: 'fulfillment_channel',
        width: 60,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => <span style={{ fontSize: 12 }}>{text}</span>
    },
    {
        title: 'ASIN',
        dataIndex: 'item_asin',
        key: 'item_asin',
        width: 110,
        render: (text: string, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return <a href={`https://www.amazon.com/dp/${text}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>{text}</a>;
        }
    },
    {
        title: 'Seller SKU',
        dataIndex: 'item_sku',
        key: 'item_sku',
        width: 140,
        render: (text: string, record: OrderTableRow) => record.isItem ? <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: text }}>{text}</Text> : null
    },
    {
        title: '商品名称',
        dataIndex: 'item_name',
        key: 'item_name',
        width: 180,
        render: (text: string, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return (
                <Tooltip title={text}>
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 160,
                        fontSize: 12
                    }}>
                        {text}
                    </div>
                </Tooltip>
            );
        }
    },
    {
        title: 'SKU',
        dataIndex: 'internal_sku_code',
        key: 'internal_sku_code',
        width: 100,
        render: (text: string, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 12 }}>{text}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
        }
    },
    {
        title: '销售员',
        dataIndex: 'owner_name',
        key: 'owner_name',
        width: 80,
        render: (text: string, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 12 }}>{text}</span> : null;
        }
    },
    {
        title: '单价',
        key: 'item_price',
        width: 90,
        render: (_: any, record: OrderTableRow) => record.isItem ? <span style={{ fontSize: 12 }}>{record.item_currency} {record.item_price}</span> : null
    },
    {
        title: 'Qty',
        dataIndex: 'item_qty',
        key: 'item_qty',
        width: 50,
        render: (text: number, record: OrderTableRow) => record.isItem ? <span style={{ fontSize: 12 }}>{text}</span> : null
    },
    {
        title: '小计',
        key: 'item_subtotal',
        width: 100,
        render: (_: any, record: OrderTableRow) => record.isItem ? <Text strong style={{ fontSize: 12 }}>{record.item_currency} {record.item_subtotal?.toFixed(2)}</Text> : null
    },
    {
        title: '采购成本',
        dataIndex: 'total_cost',
        key: 'total_cost',
        width: 100,
        render: (text: number, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 12, color: '#cf1322' }}>¥{Number(text).toFixed(2)}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
        }
    },
    {
        title: '供应商',
        dataIndex: 'supplier_name',
        key: 'supplier_name',
        width: 100,
        render: (text: string, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 12 }}>{text}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
        }
    },
    {
        title: '批次号',
        dataIndex: 'batch_number',
        key: 'batch_number',
        width: 120,
        render: (text: string, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 11 }}>{text}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
        }
    },
    {
        title: '汇率',
        dataIndex: 'exchange_rate',
        key: 'exchange_rate',
        width: 70,
        render: (text: number, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 11 }}>{Number(text).toFixed(4)}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
        }
    },
    {
        title: '人民币收入',
        dataIndex: 'revenue_cny',
        key: 'revenue_cny',
        width: 100,
        render: (text: number, record: OrderTableRow) => {
            if (!record.isItem) return null;
            return text ? <span style={{ fontSize: 12, color: '#3f8600' }}>¥{Number(text).toFixed(2)}</span> : <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
        }
    },
    {
        title: '毛利',
        dataIndex: 'gross_profit',
        key: 'gross_profit',
        width: 100,
        render: (text: number, record: OrderTableRow) => {
            if (!record.isItem) return null;
            if (text === null || text === undefined) return <span style={{ color: '#ccc', fontSize: 12 }}>-</span>;
            const color = text >= 0 ? '#3f8600' : '#cf1322';
            return <span style={{ fontSize: 12, color, fontWeight: 500 }}>¥{Number(text).toFixed(2)}</span>;
        }
    },
    {
        title: '国家',
        dataIndex: 'ship_country',
        key: 'ship_country',
        width: 60,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => <span style={{ fontSize: 12 }}>{text}</span>
    },
    {
        title: '省/州',
        dataIndex: 'ship_state',
        key: 'ship_state',
        width: 80,
        onCell: (record: OrderTableRow) => ({ rowSpan: record.rowSpan }),
        render: (text: string) => <Text style={{ fontSize: 12 }} type="secondary" ellipsis={{ tooltip: text }}>{text}</Text>
    }
];
